import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Avatar, IconButton, Tag } from "@/components/ui";
import { Img } from "@/components/media/Img";
import type { ProductSummary } from "@/api/types";
import { CATEGORY_LABELS } from "@/lib/categories";
import { cachedPalette, paletteFromImage, type CardPalette } from "./imagePalette";
import { Price } from "./Price";
import { RatingStars } from "./RatingStars";

const TAG_GAP = 6;
// Tags sit on the scrim, so fill and label come from the image palette (lifted clear of the scrim, label ≥4.5:1 on the fill);
// until the palette is ready they use the neutral translucent chip.
const OVERLAY_TAG_CLASS = "!h-5 !border-transparent !bg-[color:var(--product-card-accent-chip,color-mix(in_oklab,var(--color-surface-3)_80%,transparent))] !px-1.5 !text-caption !text-[color:var(--product-card-accent-chip-text,var(--color-text-primary))] transition-colors duration-160 ease-standard";
// On reveal, seller and category text take the palette highlight — brighter than the scrim so it stays legible.
const HIGHLIGHT_TEXT_CLASS = "transition-colors duration-160 ease-standard card-reveal:text-[color:var(--product-card-accent-highlight,var(--color-text-secondary))]";

function displayTag(tag: string) {
  return tag.replaceAll("-", " ");
}

function productCardTags(product: ProductSummary) {
  return [...new Set(product.tags ?? [])].filter((tag) => tag !== product.category);
}

function ProductTagRail({ product }: { product: ProductSummary }) {
  const tags = productCardTags(product);
  const [visibleTagCount, setVisibleTagCount] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLSpanElement>(null);
  const tagRefs = useRef<Array<HTMLSpanElement | null>>([]);

  useLayoutEffect(() => {
    const calculateVisibleTags = () => {
      const railWidth = railRef.current?.clientWidth ?? 0;
      const moreWidth = moreRef.current?.offsetWidth ?? 0;
      if (!railWidth) return;

      let usedWidth = 0;
      let nextCount = 0;
      for (const index of tags.keys()) {
        const tagWidth = tagRefs.current[index]?.offsetWidth ?? 0;
        const gap = nextCount === 0 ? 0 : TAG_GAP;
        const reserveMore = index < tags.length - 1 ? TAG_GAP + moreWidth : 0;
        if (usedWidth + gap + tagWidth + reserveMore > railWidth) break;
        usedWidth += gap + tagWidth;
        nextCount++;
      }
      setVisibleTagCount((current) => current === nextCount ? current : nextCount);
    };

    calculateVisibleTags();
    const observer = new ResizeObserver(calculateVisibleTags);
    if (railRef.current) observer.observe(railRef.current);
    return () => observer.disconnect();
  }, [product.tags, tags]);

  const visibleTags = tags.slice(0, visibleTagCount);
  const remainingTags = tags.slice(visibleTagCount);

  return (
    <div
      ref={railRef}
      data-testid="product-card-tags"
      // Hidden at rest; on reveal the rail rises 12px from the card's bottom edge as it fades in.
      className="pointer-events-none invisible absolute inset-x-3 bottom-3 flex h-5 min-w-0 translate-y-3 items-center gap-1.5 opacity-0 transition-[translate,opacity,visibility] duration-160 ease-standard card-reveal:visible card-reveal:translate-y-0 card-reveal:opacity-100"
    >
      {visibleTags.map((tag) => <Tag key={tag} className={`shrink-0 ${OVERLAY_TAG_CLASS}`}>{displayTag(tag)}</Tag>)}
      {remainingTags.length > 0 && (
        <span className="group/tags pointer-events-auto relative inline-flex shrink-0">
          <Tag className={OVERLAY_TAG_CLASS}>+{remainingTags.length}</Tag>
          <span
            role="tooltip"
            className="pointer-events-none absolute right-0 bottom-full z-40 mb-2 flex w-max max-w-56 flex-wrap gap-1 rounded-sm border border-border bg-surface-1 p-2 text-caption opacity-0 shadow-[var(--shadow-popover)] transition-opacity duration-100 ease-standard invisible group-hover/tags:visible group-hover/tags:opacity-100"
          >
            {remainingTags.map((tag) => <Tag key={tag} className="border-0 bg-surface-2">{displayTag(tag)}</Tag>)}
          </span>
        </span>
      )}
      {/* The measuring row is clipped to the rail so its full unwrapped width never widens the page scroller. */}
      <span aria-hidden="true" className="pointer-events-none invisible absolute inset-0 overflow-hidden">
        <span className="flex w-max gap-1.5 whitespace-nowrap">
          {tags.map((tag, index) => <Tag key={tag} ref={(element) => { tagRefs.current[index] = element; }} className={OVERLAY_TAG_CLASS}>{displayTag(tag)}</Tag>)}
          <Tag ref={moreRef} className={OVERLAY_TAG_CLASS}>+{tags.length}</Tag>
        </span>
      </span>
    </div>
  );
}

export function ProductCard({
  product,
  sizes = "(min-width: 1280px) 280px, (min-width: 768px) 33vw, 50vw",
}: {
  product: ProductSummary;
  sizes?: string;
}) {
  const images = [product.image, ...(product.gallery ?? []).filter((image) => image.key !== product.image.key)];
  const [selectedImage, setSelectedImage] = useState(0);
  const currentImage = images[selectedImage] ?? product.image;
  const hasGalleryControls = images.length > 1;
  const [palettes, setPalettes] = useState<Record<string, CardPalette | null>>({});
  const palette = palettes[currentImage.key] ?? cachedPalette(currentImage.key);
  const selectImage = (index: number) => setSelectedImage((index + images.length) % images.length);

  // The scrim, outline, and seller/category text read these on reveal, so their colours follow the image currently shown in the carousel.
  const paletteStyle = palette
    ? ({
        "--product-card-accent-base": palette.base,
        "--product-card-accent-tint": palette.tint,
        "--product-card-accent-highlight": palette.highlight,
        "--product-card-accent-outline": palette.outline,
        "--product-card-accent-title": palette.title,
        "--product-card-accent-chip": palette.chip,
        "--product-card-accent-chip-text": palette.chipText,
      } as CSSProperties)
    : undefined;

  return (
    <article className="product-card group relative aspect-3/2 w-full rounded-md" style={paletteStyle} data-testid="product-card">
      {hasGalleryControls && <span className="sr-only" aria-live="polite">Showing image {selectedImage + 1} of {images.length} for {product.title}</span>}
      <Link
        to="/p/$slug"
        params={{ slug: product.slug }}
        className="absolute inset-0 overflow-hidden rounded-md bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <Img
          imageKey={currentImage.key}
          alt=""
          aspect="3/2"
          sizes={sizes}
          fill
          imgClassName="transition-transform duration-100 ease-standard group-hover:scale-[1.025] group-focus-within:scale-[1.025]"
          onLoad={(event) => {
            const imageKey = currentImage.key;
            const nextPalette = paletteFromImage(imageKey, event.currentTarget);
            setPalettes((current) => imageKey in current ? current : { ...current, [imageKey]: nextPalette });
          }}
        />
        {/* 28px taller than the card so the gradient can rise with the details without exposing the image's bottom edge. */}
        <span
          aria-hidden="true"
          data-testid="product-card-scrim"
          className="product-card-scrim pointer-events-none absolute inset-x-0 top-0 h-[calc(100%+1.75rem)] card-reveal:-translate-y-7 card-reveal:[--product-card-scrim-base:var(--product-card-accent-base,var(--color-bg))] card-reveal:[--product-card-scrim-tint:var(--product-card-accent-tint,var(--color-bg))]"
        />
        {/* On reveal the details move up by the 20px tag rail + 8px gap. */}
        <div data-testid="product-card-details" className="absolute inset-x-3 bottom-3 flex flex-col gap-1 transition-[translate] duration-160 ease-standard card-reveal:-translate-y-7">
          <div className="flex h-5 items-center gap-3">
            <p
              data-testid="product-card-title"
              className="min-w-0 flex-1 truncate text-body font-semibold text-text-primary transition-colors duration-160 ease-standard card-reveal:text-[color:var(--product-card-accent-title,var(--color-text-primary))]"
            >
              {product.title}
            </p>
            <Price cents={product.price} compareAtCents={product.compareAtPrice} size="card" />
          </div>
          <div className="flex h-5 items-center gap-3">
            <span className="flex min-w-0 flex-1 items-center gap-1.5 text-small text-text-secondary">
              <Avatar size="xs" imageKey={product.seller.avatar.key} verified={product.seller.verified} />
              <span data-testid="product-card-seller" className={`min-w-0 truncate ${HIGHLIGHT_TEXT_CLASS}`}>{product.seller.displayName}</span>
              <span aria-hidden="true">·</span>
              <span data-testid="product-card-category" className={`shrink-0 ${HIGHLIGHT_TEXT_CLASS}`}>{CATEGORY_LABELS[product.category]}</span>
            </span>
            <RatingStars rating={product.rating} count={product.ratingCount} summary />
          </div>
        </div>
      </Link>
      <ProductTagRail product={product} />
      <span
        aria-hidden="true"
        data-testid="product-card-outline"
        className="pointer-events-none absolute inset-0 rounded-md border border-border transition-colors duration-160 ease-standard card-reveal:border-[color:var(--product-card-accent-outline,var(--color-border-strong))]"
      />
      {hasGalleryControls && (
        // Vertically centred on the whole card. Shares the reveal trigger, so a mouse-clicked arrow keeping focus
        // doesn't leave the arrows showing on a collapsed card.
        <div className="pointer-events-none absolute inset-x-2 inset-y-0 hidden items-center justify-between opacity-0 transition-opacity duration-100 ease-standard card-reveal:opacity-100 md:flex">
          <IconButton
            label={`Previous product image for ${product.title}`}
            icon={<CaretLeft className="size-4" weight="bold" />}
            size="sm"
            className="pointer-events-auto !rounded-full !bg-bg/70 backdrop-blur-sm hover:!bg-bg/90"
            onClick={() => selectImage(selectedImage - 1)}
          />
          <IconButton
            label={`Next product image for ${product.title}`}
            icon={<CaretRight className="size-4" weight="bold" />}
            size="sm"
            className="pointer-events-auto !rounded-full !bg-bg/70 backdrop-blur-sm hover:!bg-bg/90"
            onClick={() => selectImage(selectedImage + 1)}
          />
        </div>
      )}
    </article>
  );
}
